import {
  isValidElement,
  type ReactNode,
  type SVGProps,
  type FC,
  type Ref,
} from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { isString, mergeClasses } from "@huub-web/utils";

import { icons } from "../../theme/icons";
import type { ColorProp, IconNodeProp } from "../../props";
import type { IconName } from "../../theme";

import { gradientStyle, iconStyle } from "./Icon.css";
import { colors } from "../../theme/css/colors.css";
import type { ColorName } from "../../theme/types/ColorName";
import { theme } from "../../theme/css/theme.css";
import {
  currentColor25Style,
  currentColor50Style,
  currentColor75Style,
} from "../Text/Text.css";

export type IconProps = RecipeVariants<typeof iconStyle> &
  Omit<
    SVGProps<SVGSVGElement>,
    "viewBox" | "fill" | "xmlns" | "size" | "color" | "name"
  > &
  IconNodeProp &
  ColorProp<
    | ColorName
    | "currentColor25"
    | "currentColor50"
    | "currentColor75"
    | "gradient"
  > & {
    /**
     * @deprecated Use `icon` prop instead.
     */
    custom?: ReactNode;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<SVGSVGElement>;
  };

/**
 * Icon component is a component for displaying icons.
 */
export const Icon: FC<IconProps> = ({
  icon = "add",
  size = "m",
  color,
  className,
  custom,
  animation,
  ref,
  ...props
}) => {
  const resolvedIcon =
    isString(icon) && icon.endsWith("Filled") && !icons[icon as IconName]
      ? icon.slice(0, -6)
      : icon;

  const component = isString(resolvedIcon)
    ? icons?.[resolvedIcon as IconName]
    : isValidElement(resolvedIcon)
      ? resolvedIcon
      : custom;

  return (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={mergeClasses(
        iconStyle({ size, animation }),
        className,
        color === "currentColor50"
          ? currentColor50Style
          : color === "currentColor75"
            ? currentColor75Style
            : color === "currentColor25"
              ? currentColor25Style
              : color === "gradient"
                ? gradientStyle
                : color
                  ? colors[color]
                  : undefined,
      )}
      {...props}
    >
      {color === "gradient" && (
        <defs>
          <linearGradient
            id="huub-gradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
            gradientUnits="objectBoundingBox"
          >
            <stop offset="0%" stopColor={theme.colors.gradientA} />
            <stop offset="35%" stopColor={theme.colors.gradientB} />
            <stop offset="100%" stopColor={theme.colors.gradientC} />
          </linearGradient>
        </defs>
      )}
      {component}
    </svg>
  );
};
